import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });

let libMode = localStorage.getItem("libMode") === "owner" ? "owner" : "mine";
const libListeners = new Set();
export const getLibMode = () => libMode;
export const subscribeLibMode = (fn) => { libListeners.add(fn); return () => libListeners.delete(fn); };
export const setLibMode = (m) => {
  if (m === libMode) return;
  libMode = m;
  localStorage.setItem("libMode", m);
  libListeners.forEach((fn) => fn());
};
const LIB_GET = [/^\/albums(\/[^/]+)?$/, /^\/artists$/, /^\/artist$/, /^\/tracks$/, /^\/videos(\/.*)?$/];
export const libQs = () => (libMode === "owner" ? "?lib=owner" : "");
export const libParams = () => (libMode === "owner" ? { lib: "owner" } : {});
const owned = { albums: new Set(), tracks: new Set() };
const markOwned = (o) => {
  if (Array.isArray(o)) return o.forEach(markOwned);
  if (!o || typeof o !== "object") return;
  if (o.lib === "owner") {
    if (o.album_id) { owned.tracks.add(o.id); owned.albums.add(o.album_id); } else if (o.id) owned.albums.add(o.id);
    if (o.cover_album_id) owned.albums.add(o.cover_album_id);
  }
  if (o.cover_libs) Object.keys(o.cover_libs).forEach((id) => owned.albums.add(id));
  Object.values(o).forEach((v) => { if (v && typeof v === "object") markOwned(v); });
};

api.interceptors.request.use((config) => {
  if (libMode === "owner" && (config.method || "get") === "get" && LIB_GET.some((r) => r.test(config.url || "")) && !config.params?.starred) {
    config.params = { ...(config.params || {}), lib: "owner" };
  }
  return config;
});

let refreshing = null;
api.interceptors.response.use(
  (r) => { if ((r.config.method || "get") === "get") markOwned(r.data); return r; },
  async (error) => {
    const orig = error.config;
    if (error.response?.status === 401 && !orig._retry && !orig.url.includes("/auth/")) {
      orig._retry = true;
      refreshing = refreshing || api.post("/auth/refresh").finally(() => (refreshing = null));
      try {
        await refreshing;
        return api(orig);
      } catch (e) {
        window.dispatchEvent(new Event("auth:logout"));
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail.msg || String(detail);
}

const shared = { albums: {}, tracks: {} };
const OFFLINE_URLS_KEY = "rh.offlineUrls";
let offlineUrls = {};
try { offlineUrls = JSON.parse(localStorage.getItem(OFFLINE_URLS_KEY) || "{}"); } catch { offlineUrls = {}; }
export const setOfflineUrls = (map) => { offlineUrls = map; try { localStorage.setItem(OFFLINE_URLS_KEY, JSON.stringify(map)); } catch { /* quota */ } };
export const registerShared = (pid, tracks) => {
  tracks.forEach((t) => { shared.tracks[t.id] = pid; if (t.album_id) shared.albums[t.album_id] = pid; });
};
export const registerSharedAlbums = (pid, albumIds) => albumIds.forEach((id) => { shared.albums[id] = shared.albums[id] || pid; });
export const coverUrl = (albumId) => (shared.albums[albumId]
  ? `${API}/shared/playlists/${shared.albums[albumId]}/albums/${albumId}/cover`
  : `${API}/albums/${albumId}/cover${owned.albums.has(albumId) ? "?lib=owner" : ""}`);
export const artistPhotoUrl = (name, version, lib) => `${API}/artists/photo?name=${encodeURIComponent(name)}&v=${version}${lib === "owner" ? "&lib=owner" : ""}`;
export const transcoded = new Set();
const probe = typeof document !== "undefined" ? document.createElement("audio") : null;
const noAac = probe ? !probe.canPlayType('audio/mp4; codecs="mp4a.40.2"') : false;
const tail = (trackId) => {
  const q = [owned.tracks.has(trackId) && "lib=owner", transcoded.has(trackId) && noAac && "fmt=flac"].filter(Boolean);
  return q.length ? `?${q.join("&")}` : "";
};
export const streamUrl = (trackId) => offlineUrls[trackId] || (shared.tracks[trackId]
  ? `${API}/shared/playlists/${shared.tracks[trackId]}/tracks/${trackId}/stream`
  : `${API}/tracks/${trackId}/${transcoded.has(trackId) ? "transcode" : "stream"}${tail(trackId)}`);

export function fmtTime(s) {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  if (m >= 60) return `${Math.floor(m / 60)}:${(m % 60).toString().padStart(2, "0")}:${sec}`;
  return `${m}:${sec}`;
}

export const downloadFile = async (path) => {
  await api.get("/auth/me");
  const a = document.createElement("a");
  a.href = `${API}${path}`;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
};
